/**
 * Audio Service for Tutorial Text-to-Speech
 * Provides audio generation and playback functionality
 */

export interface AudioGenerationOptions {
  voice?: string;
  speed?: number;
  pitch?: number;
  volume?: number;
  language?: string;
  provider?: 'elevenlabs' | 'browser';
  model?: string;
}

export interface AudioPlayerState {
  isPlaying: boolean;
  isPaused: boolean;
  isStopped: boolean;
  currentTime: number;
  duration: number;
  progress: number;
}

export class AudioService {
  private static instance: AudioService;
  // private audioContext: AudioContext | null = null;
  private currentAudio: HTMLAudioElement | null = null;
  private isSupported: boolean = false;
  private elevenLabsApiKey: string | null = null;
  private audioCache: Map<string, string> = new Map();
  private apiBaseUrl: string;

  constructor() {
    this.checkSupport();
    // Always use backend for ElevenLabs - no frontend API key needed
    this.elevenLabsApiKey = 'backend'; // Always available via backend
    const raw = import.meta.env.VITE_VIAPHOTON_API_URL || '/api';
    let normalized = raw.replace(/\/$/, '');
    const needsApiSuffix = /localhost:3001$/.test(normalized) || /kb-api\./.test(normalized);
    if (needsApiSuffix && !/\/api$/.test(normalized)) {
      normalized += '/api';
    }
    this.apiBaseUrl = normalized;
  }

  static getInstance(): AudioService {
    if (!AudioService.instance) {
      AudioService.instance = new AudioService();
    }
    return AudioService.instance;
  }

  private checkSupport(): void {
    this.isSupported = 'speechSynthesis' in window;
    // AudioContext can be used for advanced audio processing if needed
    // if (this.isSupported) {
    //   this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    // }
  }

  /**
   * Clean text by removing Markdown formatting and symbols
   */
  private cleanTextForAudio(text: string): string {
    return text
      // Remove Markdown headers
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold/italic formatting
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/_([^_]+)_/g, '$1')
      // Remove code blocks and inline code
      .replace(/```[\s\S]*?```/g, '')
      .replace(/`([^`]+)`/g, '$1')
      // Remove links
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove images
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '')
      // Remove blockquotes
      .replace(/^>\s*/gm, '')
      // Remove list markers
      .replace(/^[\s]*[-*+]\s+/gm, '')
      .replace(/^[\s]*\d+\.\s+/gm, '')
      // Remove horizontal rules
      .replace(/^[-*_]{3,}$/gm, '')
      // Remove HTML tags
      .replace(/<[^>]*>/g, '')
      // Remove extra whitespace and normalize
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Generate audio from text using ElevenLabs or Web Speech API
   */
  async generateAudio(
    text: string, 
    options: AudioGenerationOptions = {}
  ): Promise<string> {
    // Clean the text before generating audio
    const cleanText = this.cleanTextForAudio(text);
    
    const provider = options.provider || (this.elevenLabsApiKey ? 'elevenlabs' : 'browser');
    if (provider === 'elevenlabs' && this.elevenLabsApiKey) {
      return this.generateElevenLabsAudio(cleanText, options);
    } else {
      return this.generateBrowserAudio(cleanText, options);
    }
  }

  /**
   * Generate audio using ElevenLabs API
   */
  private async generateElevenLabsAudio(
    text: string,
    options: AudioGenerationOptions
  ): Promise<string> {
    
    // Check cache first (use cleaned text for cache key)
    const cleanText = this.cleanTextForAudio(text);
    const cacheKey = `${cleanText}-${options.voice}-${options.speed}`;
    if (this.audioCache.has(cacheKey)) {
      const cachedUrl = this.audioCache.get(cacheKey)!;
      return this.playAudioFromUrl(cachedUrl);
    }

    try {
      const requestBody = {
        text: cleanText,
        voice_id: options.voice || 'pNInz6obpgDQGcFmaJgB', // Default Adam voice
        model_id: options.model || 'eleven_monolingual_v1',
        voice_settings: {
          stability: 0.5,
          similarity_boost: 0.5,
          style: 0.0,
          use_speaker_boost: true
        }
      };
      
      
      const response = await fetch(`${this.apiBaseUrl}/audio/elevenlabs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error('🎤 AudioService: ElevenLabs API error response:', errorText);
        throw new Error(`ElevenLabs API error: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Cache the audio
      this.audioCache.set(cacheKey, audioUrl);
      
      return this.playAudioFromUrl(audioUrl);
    } catch (error) {
      console.error('🎤 AudioService: ElevenLabs failed, falling back to browser TTS:', error);
      return this.generateBrowserAudio(text, options);
    }
  }

  /**
   * Generate audio using browser Web Speech API
   */
  private async generateBrowserAudio(
    text: string,
    options: AudioGenerationOptions
  ): Promise<string> {
    if (!this.isSupported) {
      throw new Error('Speech synthesis not supported in this browser');
    }

    // Clean the text for browser TTS as well
    const cleanText = this.cleanTextForAudio(text);

    return new Promise((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance(cleanText);
      
      // Set voice options
      utterance.voice = this.getVoice(options.voice, options.language);
      utterance.rate = options.speed || 1.0;
      utterance.pitch = options.pitch || 1.0;
      utterance.volume = options.volume || 1.0;
      utterance.lang = options.language || 'en-US';

      // Handle completion
      utterance.onend = () => {
        resolve('audio-generated');
      };

      utterance.onerror = (event) => {
        reject(new Error(`Speech synthesis failed: ${event.error}`));
      };

      // Start synthesis
      speechSynthesis.speak(utterance);
    });
  }

  /**
   * Play audio from URL
   */
  private async playAudioFromUrl(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio = null;
      }

      this.currentAudio = new Audio(url);
      
      this.currentAudio.onloadeddata = () => {
        this.currentAudio?.play().then(() => resolve('audio-playing')).catch(reject);
      };

      this.currentAudio.onerror = () => {
        reject(new Error('Failed to load audio'));
      };
    });
  }

  /**
   * Get available voices (ElevenLabs + Browser)
   */
  async getAvailableVoices(): Promise<Array<{id: string, name: string, provider: string, language?: string}>> {
    const voices = [];
    
    // Add ElevenLabs voices if API key is available
    if (this.elevenLabsApiKey) {
      try {
        const elevenLabsVoices = await this.getElevenLabsVoices();
        voices.push(...elevenLabsVoices);
      } catch (error) {
        console.warn('Failed to fetch ElevenLabs voices:', error);
      }
    }
    
    // Add browser voices
    const browserVoices = speechSynthesis.getVoices().map(voice => ({
      id: voice.name,
      name: voice.name,
      provider: 'browser',
      language: voice.lang
    }));
    voices.push(...browserVoices);
    
    return voices;
  }

  /**
   * Get ElevenLabs voices
   */
  private async getElevenLabsVoices(): Promise<Array<{id: string, name: string, provider: string}>> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/audio/elevenlabs/voices`, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      
      if (!response.ok) {
        console.warn('🎤 AudioService: ElevenLabs voices response not OK:', response.status, response.statusText);
        // Backend might return a fallback list or an error – try JSON, otherwise return []
        try {
          const maybeJson = await response.json();
          if (maybeJson && Array.isArray(maybeJson.voices)) {
            return maybeJson.voices.map((voice: any) => ({
              id: voice.voice_id,
              name: voice.name,
              provider: 'elevenlabs'
            }));
          }
        } catch (_e) {
          console.warn('🎤 AudioService: ElevenLabs voices fallback JSON parse failed:', _e);
          // Non-JSON (e.g., HTML error page). Swallow and fallback.
        }
        return [];
      }
      const contentType = response.headers.get('content-type') || '';
      
      if (!contentType.includes('application/json')) {
        // Avoid JSON parse error on HTML error pages served by proxies/CDNs
        const text = await response.text();
        console.warn('🎤 AudioService: Unexpected content-type from /api/audio/elevenlabs/voices:', contentType, text?.slice(0, 200));
        return [];
      }
      const data = await response.json();
      return (data.voices || []).map((voice: any) => ({
        id: voice.voice_id,
        name: voice.name,
        provider: 'elevenlabs'
      }));
    } catch (error) {
      console.error('🎤 AudioService: Error fetching ElevenLabs voices:', error);
      return [];
    }
  }

  /**
   * Get the best voice for the given language
   */
  private getVoice(preferredVoice?: string, language?: string): SpeechSynthesisVoice | null {
    const voices = speechSynthesis.getVoices();
    
    if (preferredVoice) {
      const voice = voices.find(v => v.name === preferredVoice);
      if (voice) return voice;
    }

    if (language) {
      const voice = voices.find(v => v.lang.startsWith(language));
      if (voice) return voice;
    }

    // Default to first English voice
    return voices.find(v => v.lang.startsWith('en')) || voices[0] || null;
  }

  /**
   * Play audio from URL
   */
  async playAudio(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio = null;
      }

      this.currentAudio = new Audio(url);
      
      this.currentAudio.onloadeddata = () => {
        this.currentAudio?.play().then(resolve).catch(reject);
      };

      this.currentAudio.onerror = () => {
        reject(new Error('Failed to load audio'));
      };
    });
  }

  /**
   * Stop current audio
   */
  stopAudio(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    speechSynthesis.cancel();
  }

  /**
   * Pause current audio
   */
  pauseAudio(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
    }
    speechSynthesis.pause();
  }

  /**
   * Resume current audio
   */
  resumeAudio(): void {
    if (this.currentAudio) {
      this.currentAudio.play();
    }
    speechSynthesis.resume();
  }

  /**
   * Get current audio state
   */
  getAudioState(): AudioPlayerState {
    const isPlaying = this.currentAudio ? !this.currentAudio.paused : speechSynthesis.speaking;
    const isPaused = this.currentAudio ? this.currentAudio.paused : speechSynthesis.paused;
    const isStopped = !isPlaying && !isPaused;
    
    return {
      isPlaying,
      isPaused,
      isStopped,
      currentTime: this.currentAudio?.currentTime || 0,
      duration: this.currentAudio?.duration || 0,
      progress: this.currentAudio ? (this.currentAudio.currentTime / this.currentAudio.duration) * 100 : 0
    };
  }

  /**
   * Check if audio is supported
   */
  isAudioSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Check if ElevenLabs is available
   */
  isElevenLabsAvailable(): boolean {
    return this.elevenLabsApiKey !== null;
  }

  /**
   * Get the preferred provider
   */
  getPreferredProvider(): 'elevenlabs' | 'browser' {
    return this.isElevenLabsAvailable() ? 'elevenlabs' : 'browser';
  }

  /**
   * Get estimated duration for text
   */
  estimateDuration(text: string, wordsPerMinute: number = 150): number {
    const wordCount = text.split(/\s+/).length;
    return (wordCount / wordsPerMinute) * 60; // in seconds
  }
}

export default AudioService;
